import { connectSocket } from "./socket";

// Client side of the firmware settings channel (issue #50; contract in
// KhronosFirmware#52). The device is the source of truth: writes resolve on
// the device's settings:ack (with the effective value after clamping), and
// current values arrive as settings:snapshot broadcasts — callers must never
// render optimistic state.
//
// Listeners are (re)bound lazily on every public call because the socket
// singleton can be torn down and recreated across live sessions
// (disconnectSocket() in SocialContext) — binding once at import time would
// strand the handlers on a dead socket instance.

const ACK_TIMEOUT_MS = 5000;

const pendingAcks = new Map(); // req_id → { resolve, reject, timeoutId }
const snapshotSubs = new Set();
const ackSubs = new Set();

let boundSocket = null;

const newReqId = () => crypto.randomUUID().replace(/-/g, "").slice(0, 12);

function onSnapshot(snapshot) {
  for (const cb of snapshotSubs) cb(snapshot);
}

function onAck(ack) {
  const entry = pendingAcks.get(ack.req_id);
  if (entry) {
    clearTimeout(entry.timeoutId);
    pendingAcks.delete(ack.req_id);
    if (ack.ok) entry.resolve(ack);
    else entry.reject(Object.assign(new Error(ack.error || "device_error"), { ack }));
  }
  for (const cb of ackSubs) cb(ack);
}

function bind() {
  const socket = connectSocket();
  if (!socket) return null;
  if (socket !== boundSocket) {
    boundSocket = socket;
    socket.on("settings:snapshot", onSnapshot);
    socket.on("settings:ack", onAck);
  }
  return socket;
}

function emitWithAck(event, payload, timeoutMs) {
  const socket = bind();
  if (!socket) return Promise.reject(new Error("no_socket"));
  const reqId = newReqId();
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pendingAcks.delete(reqId);
      reject(new Error("ack_timeout"));
    }, timeoutMs);
    pendingAcks.set(reqId, { resolve, reject, timeoutId });
    socket.emit(event, { ...payload, req_id: reqId });
  });
}

// Ask a device for a fresh snapshot. Fire-and-forget: an online device
// answers with a broadcast snapshot, an offline one gets answered by the
// server's cache (marked cached:true), and firmware without the settings
// channel stays silent — that silence is what hides the panel.
export function requestSettingsSnapshot(deviceId) {
  const socket = bind();
  if (socket) socket.emit("settings:get", { device_id: deviceId });
}

export function sendSettingsSet(deviceId, key, value, { timeoutMs = ACK_TIMEOUT_MS } = {}) {
  return emitWithAck("settings:set", { device_id: deviceId, key, value }, timeoutMs);
}

export function sendSettingsCmd(deviceId, action, { timeoutMs = ACK_TIMEOUT_MS } = {}) {
  return emitWithAck("settings:cmd", { device_id: deviceId, action }, timeoutMs);
}

// Subscriptions return an unsubscribe function. Acks are surfaced to
// subscribers as well as pending promises so the panel can fold effective
// values (e.g. a clamped brightness) into its rendered state.
export function subscribeSettingsSnapshots(cb) {
  snapshotSubs.add(cb);
  bind();
  return () => snapshotSubs.delete(cb);
}

export function subscribeSettingsAcks(cb) {
  ackSubs.add(cb);
  bind();
  return () => ackSubs.delete(cb);
}

// Per-device presence, piggybacked here so the panel can grey out / re-query
// without duplicating socket plumbing. Presence events are server-pushed on
// device connect/disconnect (plus a replay when the browser socket connects).
const presenceSubs = new Set();
let presenceBoundSocket = null;

function onPresence(evt) {
  for (const cb of presenceSubs) cb(evt);
}

export function subscribeDevicePresence(cb) {
  const socket = connectSocket();
  if (socket && socket !== presenceBoundSocket) {
    presenceBoundSocket = socket;
    socket.on("device:presence", onPresence);
  }
  presenceSubs.add(cb);
  return () => presenceSubs.delete(cb);
}

// Why a queued firmware install is not running (KhronosFirmware#68). Pushed by
// the server on every change, over both channels the device can report on: the
// live socket while it holds one, and POST /api/devices/config/nack once it has
// torn that down for the install attempt. The firmware card used to read this
// only from its mount-time fetch, so a reason raised while the page was open
// sat unseen until a manual reload.
const otaStatusSubs = new Set();
let otaStatusBoundSocket = null;

function onOtaStatus(evt) {
  for (const cb of otaStatusSubs) cb(evt);
}

export function subscribeOtaStatus(cb) {
  const socket = connectSocket();
  if (socket && socket !== otaStatusBoundSocket) {
    otaStatusBoundSocket = socket;
    socket.on("ota:status", onOtaStatus);
  }
  otaStatusSubs.add(cb);
  return () => otaStatusSubs.delete(cb);
}

// The device's own 1 Hz state heartbeat (`live:device_state`), carrying its
// sensor readings — hr_bpm, env.temperature_c, env.humidity_pct. Piggybacked
// here for the same reason as presence: the Devices panel wants it without
// taking a dependency on SocialContext's live-session plumbing.
//
// The firmware only streams this while a session is running, so subscribers
// must treat "no event yet" as "not streaming", not as "still loading" —
// GET /api/devices/:id/live is deprecated and answers idle devices with a
// 200 and a null body, which is what used to hang the panel on a spinner.
const deviceStateSubs = new Set();
let deviceStateBoundSocket = null;

function onDeviceState(evt) {
  for (const cb of deviceStateSubs) cb(evt);
}

export function subscribeDeviceState(cb) {
  const socket = connectSocket();
  if (socket && socket !== deviceStateBoundSocket) {
    deviceStateBoundSocket = socket;
    socket.on("live:device_state", onDeviceState);
  }
  deviceStateSubs.add(cb);
  return () => deviceStateSubs.delete(cb);
}
